import { ChangeEvent, memo } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { formatNumber } from "../../utils";
import { formatThaiShortDate } from "../../utils/date";
import { Branch } from "../../types/user.type";
import {
  PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL,
  PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL,
  PurchaseOrderImportPreviewRow,
} from "../../types/purchase-order.type";
import BranchChoiceField, { branchOptionLabel } from "./BranchChoiceField";
import {
  BRANCH_REQUIRED_RESULT,
  DUPLICATE_RESULT,
  MATCHED_RESULT,
  ROW_RESULT_CHIP_COLOR,
} from "./constants";
import { isSelectableRow } from "./selection";

const EMPTY_VALUE = "–";

/** ข้อความสั้นที่ห้ามขึ้นบรรทัดใหม่ — ตารางเป็น fixed layout ความกว้างจึงคงที่อยู่แล้ว */
const NOWRAP_SX = { whiteSpace: "nowrap" } as const;

/**
 * ความสูงคงที่ของเนื้อหาในช่อง สาขา / ร้าน · dropdown, "ไม่นำเข้า" และสาขา + ร้านสองบรรทัด
 * สูงไม่เท่ากัน ถ้าปล่อยตามเนื้อหา แถวจะยืด/หดทุกครั้งที่ติ๊กนำเข้า
 */
const BRANCH_CELL_HEIGHT = 40;

/** ข้อความยาวตัดด้วย … และมี title ให้ชี้ดูเต็ม */
const ellipsisSx = { display: "block" } as const;

interface PreviewRowProps {
  row: PurchaseOrderImportPreviewRow;
  branches: Branch[];
  /** company_id ที่เลือกไว้ให้แถวนี้ · "" = ยังไม่เลือก */
  chosenCompanyId: string;
  onChooseBranch: (rowNo: number, companyId: string) => void;
  /** ติ๊กเลือกไว้สำหรับทำกับหลายแถวพร้อมกัน (มีทุกแถวยกเว้นแถวซ้ำ) */
  selected: boolean;
  /** shiftKey = เลือกเป็นช่วงจากแถวที่คลิกล่าสุด */
  onToggleSelect: (rowNo: number, shiftKey: boolean) => void;
  /** แถวเกินกำหนดส่งที่ติ๊กนำเข้าแล้ว */
  pastIncluded: boolean;
  onTogglePast: (rowNo: number) => void;
}

/**
 * หนึ่งแถวของตารางตัวอย่าง W2 — ข้อมูลที่อ่านได้จากไฟล์ + ผลตรวจของแถวนั้น
 *
 * ช่องติ๊กซ้ายมือมีทุกแถวที่บันทึกได้ (ไม่ใช่แถวซ้ำ) · แถว "ต้องเลือกสาขา" ที่จะถูกบันทึก
 * มี dropdown · แถวที่จับคู่ร้านได้แสดงสาขาและร้านที่ระบบหาเจอ
 *
 * ห่อด้วย memo: ไฟล์จริงมี ~8,700 แถว และการติ๊กแถวเดียวต้องไม่ render ทั้งหน้าใหม่ ·
 * ผู้เรียกต้องส่ง callback ที่คงที่ (useCallback) ไม่งั้น memo ไม่มีผล
 */
const PreviewRow = ({
  row,
  branches,
  chosenCompanyId,
  onChooseBranch,
  selected,
  onToggleSelect,
  pastIncluded,
  onTogglePast,
}: PreviewRowProps) => {
  const handleChooseBranch = (companyId: string) => {
    onChooseBranch(row.row_no, companyId);
  };

  // checkbox ยิง change จากการคลิก nativeEvent จึงบอกได้ว่ากด Shift ค้างไว้หรือไม่
  const handleToggleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const native = event.nativeEvent as MouseEvent | undefined;
    onToggleSelect(row.row_no, Boolean(native?.shiftKey));
  };

  const handleTogglePast = () => {
    onTogglePast(row.row_no);
  };

  // แถวเกินกำหนดส่งที่ไม่ได้ติ๊กนำเข้าถูกข้ามตอนยืนยัน จึงไม่ต้องเลือกสาขา
  const pastImportable = row.past_delivery && row.result !== DUPLICATE_RESULT;
  const skippedPast = pastImportable && !pastIncluded;
  const needsBranch = row.result === BRANCH_REQUIRED_RESULT && !skippedPast;
  const deliveryDate =
    formatThaiShortDate(row.delivery_date, { weekday: false }) ||
    row.delivery_date;
  const matchedCustomer = row.result === MATCHED_RESULT ? row.customer : null;
  const shipToName = row.ship_to_name || EMPTY_VALUE;
  const branchText = row.branch ? branchOptionLabel(row.branch) : EMPTY_VALUE;
  const shopText = matchedCustomer
    ? `${matchedCustomer.customer_code ?? EMPTY_VALUE} ${
        matchedCustomer.customer_name ?? ""
      }`.trim()
    : "";

  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        {isSelectableRow(row) && (
          <Checkbox
            size="small"
            checked={selected}
            onChange={handleToggleSelect}
            inputProps={{ "aria-label": `เลือกแถว ${row.row_no}` }}
          />
        )}
      </TableCell>
      {/* เลขแถวใน Excel เป็นตำแหน่งสำหรับเปิดไฟล์ไปดู ไม่ใช่จำนวน จึงไม่ใส่ลูกน้ำ */}
      <TableCell sx={NOWRAP_SX}>{row.row_no}</TableCell>
      <TableCell sx={NOWRAP_SX}>{row.po_number}</TableCell>
      <TableCell sx={NOWRAP_SX}>{deliveryDate}</TableCell>
      <TableCell sx={NOWRAP_SX}>{row.ship_to_code}</TableCell>
      <TableCell>
        <Typography variant="body2" noWrap title={shipToName} sx={ellipsisSx}>
          {shipToName}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={NOWRAP_SX}>
        {formatNumber(row.amount)}
      </TableCell>
      <TableCell>
        {/* เนื้อหาในช่องนี้ขึ้นกับข้อมูลของแถวเท่านั้น (ไม่ขึ้นกับการติ๊ก) ความสูงจึงคงที่ */}
        <Stack gap={0.5} alignItems="flex-start">
          <Chip
            size="small"
            variant="outlined"
            color={ROW_RESULT_CHIP_COLOR[row.result]}
            label={PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL[row.result]}
          />
          {row.past_delivery && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL}
            />
          )}
          {pastImportable && (
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={pastIncluded}
                  onChange={handleTogglePast}
                  sx={{ p: 0.5 }}
                />
              }
              label={<Typography variant="caption">นำเข้า</Typography>}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Box
          sx={{
            height: BRANCH_CELL_HEIGHT,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          {needsBranch && (
            <BranchChoiceField
              candidates={row.branch_candidates}
              branches={branches}
              value={chosenCompanyId}
              onChange={handleChooseBranch}
            />
          )}

          {skippedPast && (
            <Typography variant="body2" color="text.secondary" noWrap>
              ไม่นำเข้า ({PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL})
            </Typography>
          )}

          {!needsBranch && !skippedPast && (
            <>
              <Typography
                variant="body2"
                noWrap
                title={branchText}
                sx={ellipsisSx}
              >
                {branchText}
              </Typography>
              {shopText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={shopText}
                  sx={ellipsisSx}
                >
                  {shopText}
                </Typography>
              )}
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default memo(PreviewRow);
